import { useForm } from "@tanstack/react-form";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { createFileRoute, Link, redirect } from "@tanstack/react-router";
import { useState } from "react";
import { z } from "zod";

import type { EmailConfirmationRequiredResponse } from "@ravnar/api-contract";

import { createOrganization, sessionQueryOptions, startSession } from "../../api/session";
import { ConfirmEmailForm, type SignUpCredentials } from "../../components/confirm-email-form";
import { PageHeader } from "../../components/shell/app-shell";
import { PublicPage } from "../../components/shell/public-page";
import { Button } from "../../components/ui/button";
import { Card } from "../../components/ui/card";
import { Field, Input } from "../../components/ui/field";
import { IDENTITY_FIELD_LABELS } from "../../domain/settings";
import { fieldErrorMessage } from "../../lib/form";

const createOrganizationFormSchema = z.object({
	companyName: z
		.string()
		.trim()
		.min(1, `${IDENTITY_FIELD_LABELS.companyName} is required`)
		.max(120, "Use at most 120 characters"),
	name: z.string().trim().min(1, "Your name is required").max(80, "Use at most 80 characters"),
	email: z.email("Enter a valid email address"),
	password: z.string().min(12, "Use at least 12 characters").max(128, "Use at most 128 characters"),
});

export const Route = createFileRoute("/organizations/new")({
	// Someone who is signed in already belongs to an organization.
	beforeLoad: async ({ context }) => {
		const session = await context.queryClient.query(sessionQueryOptions());
		if (session !== null) {
			throw redirect({ to: "/" });
		}
	},
	head: () => ({ meta: [{ title: "New organization · Ravnar" }] }),
	staticData: { public: true },
	component: CreateOrganizationPage,
});

function CreateOrganizationPage() {
	const navigate = Route.useNavigate();
	const queryClient = useQueryClient();
	const mutation = useMutation({ mutationFn: createOrganization });
	// Set once the API has emailed the code. The password goes back to the API with the code.
	const [signUp, setSignUp] = useState<SignUpCredentials | undefined>();

	const form = useForm({
		defaultValues: { companyName: "", name: "", email: "", password: "" },
		// Zod schemas are Standard Schema validators, so no adapter is needed.
		validators: { onChange: createOrganizationFormSchema, onSubmit: createOrganizationFormSchema },
		onSubmit: async ({ value }) => {
			// parse() applies the schema's trimming, so surrounding spaces are not sent.
			const request = createOrganizationFormSchema.parse(value);
			let response: EmailConfirmationRequiredResponse;
			try {
				response = await mutation.mutateAsync(request);
			} catch {
				// The alert below shows mutation.error. Rethrowing would make handleSubmit reject unhandled.
				return;
			}
			setSignUp({ email: response.email, password: request.password });
		},
	});

	if (signUp !== undefined) {
		return (
			<PublicPage>
				<Card className="p-6">
					<ConfirmEmailForm
						email={signUp.email}
						password={signUp.password}
						onSignedIn={async session => {
							startSession(queryClient, session);
							await navigate({ to: "/", replace: true });
						}}
						onStartAgain={() => {
							// The form keeps what was typed.
							setSignUp(undefined);
						}}
					/>
				</Card>
			</PublicPage>
		);
	}

	return (
		<PublicPage>
			<Card className="p-6">
				<PageHeader title="New organization" description="Set up your company on Ravnar as its owner." />
				<form
					noValidate
					className="flex flex-col gap-4"
					onSubmit={event => {
						event.preventDefault();
						event.stopPropagation();
						void form.handleSubmit();
					}}
				>
					<form.Field name="companyName">
						{field => (
							<Field
								label={IDENTITY_FIELD_LABELS.companyName}
								error={fieldErrorMessage(field.state.meta)}
							>
								<Input
									name={field.name}
									autoComplete="organization"
									value={field.state.value}
									onBlur={field.handleBlur}
									onChange={event => {
										field.handleChange(event.target.value);
									}}
								/>
							</Field>
						)}
					</form.Field>
					<form.Field name="name">
						{field => (
							<Field label="Your name" error={fieldErrorMessage(field.state.meta)}>
								<Input
									name={field.name}
									autoComplete="name"
									value={field.state.value}
									onBlur={field.handleBlur}
									onChange={event => {
										field.handleChange(event.target.value);
									}}
								/>
							</Field>
						)}
					</form.Field>
					<form.Field name="email">
						{field => (
							<Field label="Email" error={fieldErrorMessage(field.state.meta)}>
								<Input
									type="email"
									name={field.name}
									autoComplete="username"
									value={field.state.value}
									onBlur={field.handleBlur}
									onChange={event => {
										field.handleChange(event.target.value);
									}}
								/>
							</Field>
						)}
					</form.Field>
					<form.Field name="password">
						{field => (
							<Field label="Password" error={fieldErrorMessage(field.state.meta)}>
								<Input
									type="password"
									name={field.name}
									autoComplete="new-password"
									value={field.state.value}
									onBlur={field.handleBlur}
									onChange={event => {
										field.handleChange(event.target.value);
									}}
								/>
							</Field>
						)}
					</form.Field>
					<p className="text-caption text-ink-3">Use at least 12 characters.</p>
					{mutation.isError ? (
						<p role="alert" className="text-caption text-alert">
							{mutation.error.message}
						</p>
					) : null}
					<form.Subscribe selector={state => [state.canSubmit, state.isSubmitting]}>
						{([canSubmit, isSubmitting]) => (
							<Button
								type="submit"
								variant="primary"
								className="w-full justify-center"
								disabled={!canSubmit || isSubmitting}
							>
								{isSubmitting ? "Creating…" : "Create organization"}
							</Button>
						)}
					</form.Subscribe>
				</form>
				<p className="mt-5 border-t border-line pt-4 text-center text-body text-ink-2">
					Already have an account?{" "}
					<Link to="/login" className="font-medium text-accent hover:underline">
						Sign in
					</Link>
				</p>
			</Card>
		</PublicPage>
	);
}
